import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getColors, fonts } from '../theme';
import { 
  Waves, 
  Sun, 
  Wind, 
  CheckCircle2, 
  Calendar, 
  Target, 
  Sparkles,
  ArrowRight,
  Shield,
  Smartphone,
  Moon,
  Flame,
  Sunrise,
  CloudSun,
  Quote,
  ClipboardList,
  CalendarDays
} from 'lucide-react';
import Logo from './Logo';

const SurferLandingPage = ({ isDark = true, onGetStarted }) => {
  const colors = getColors(isDark);
  const [isVisible, setIsVisible] = useState(false);
  const [currentQuote, setCurrentQuote] = useState(0);
  const [isQuoteFading, setIsQuoteFading] = useState(false);

  const quotes = [
    {
      text: "You can't stop the waves, but you can learn to surf.",
      author: "Jon Kabat-Zinn"
    },
    {
      text: "The best surfer out there is the one having the most fun.",
      author: "Duke Kahanamoku"
    },
    {
      text: "Surfing is attitude dancing.",
      author: "Gerry Lopez"
    },
    {
      text: "Out of the water, I am nothing.",
      author: "Duke Kahanamoku"
    }
  ];

  useEffect(() => {
    // Set page-specific title for surfer landing
    document.title = 'mind. | Build Better Habits, Catch More Waves';
    
    return () => {
      // Reset to default on unmount
      document.title = 'mind.';
    };
  }, []);

  useEffect(() => {
    setIsVisible(true);
    
    // Rotate quotes every 8 seconds with fade transition
    const interval = setInterval(() => {
      setIsQuoteFading(true);
      setTimeout(() => {
        setCurrentQuote(prev => (prev + 1) % quotes.length);
        setIsQuoteFading(false);
      }, 500); // Wait for fade out before changing
    }, 8000);
    
    return () => clearInterval(interval);
  }, []);

  const benefits = [
    {
      icon: CheckCircle2,
      title: "One habit. That's it.",
      description: "Stretch after every session. Breath holds before bed. Pick one.",
      color: colors.accent
    },
    {
      icon: Target,
      title: "Streaks, not schedules",
      description: "See your consistency build like swell on the horizon",
      color: colors.textSecondary
    },
    {
      icon: Calendar,
      title: "Your pace. Your life.",
      description: "No alarms. No pressure. Just one less excuse when the waves are firing.",
      color: colors.accent
    }
  ];

  return (
    <div 
      className="min-h-screen relative overflow-hidden"
      style={{ backgroundColor: colors.bg }}
    >

      {/* Simple Navigation */}
      <nav className="px-6 max-w-5xl mx-auto relative z-10">
        <div className="flex items-end justify-between" style={{ borderBottom: `1px solid ${colors.textPrimary}` }}>
          <span className="text-[22px] leading-none pt-5 pb-3.5" style={{ fontFamily: fonts.serif, color: colors.textPrimary }}>mind.</span>
          <button onClick={onGetStarted} className="text-sm pb-3.5 hover:underline underline-offset-4" style={{ color: colors.textSecondary }}>Sign in</button>
        </div>
      </nav>

      {/* Hero Section - Breathing Room */}
      <section className="px-6 pt-8 pb-16 md:pt-16 md:pb-24 max-w-4xl mx-auto relative z-10">
        <div 
          className={`text-center transition-all duration-300 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Calm tagline */}
          <div 
            className="inline-flex items-center gap-2 font-mono text-xs mb-8"
            style={{ color: colors.textMuted }}
          >
            <Sun size={16} />
            <span>Reclaim your focus</span>
          </div>

          {/* Quote - The Hook */}
          <div className="mb-12 min-h-[180px] md:min-h-[200px] flex items-center justify-center relative">
            <blockquote 
              className="text-2xl md:text-3xl lg:text-4xl leading-relaxed transition-opacity duration-200 ease-in-out font-semibold tracking-[-0.035em]"
              style={{ 
                color: colors.textPrimary,
                opacity: isQuoteFading ? 0 : 1
              }}
            >
              "{quotes[currentQuote].text}"
              <footer 
                className="text-base md:text-lg mt-4 font-normal transition-opacity duration-200"
                style={{ color: colors.textMuted, opacity: isQuoteFading ? 0 : 1 }}
              >
                - {quotes[currentQuote].author}
              </footer>
            </blockquote>
          </div>

          {/* The gentle pitch - not pushy */}
          <p 
            className={`text-lg md:text-xl mb-12 max-w-2xl mx-auto leading-relaxed transition-all duration-300 delay-200 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
            style={{ color: colors.textSecondary }}
          >
            You can't control the swell. But you can control whether you 
            stretched today. <span style={{ color: colors.accent }}>mind.</span> helps you track the 
            small things, so you're ready when it's on.
          </p>

          {/* Single, calm CTA */}
          <div 
            className={`transition-all duration-300 delay-400 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
          >
            <button
              onClick={onGetStarted}
              className="group inline-flex items-center gap-3 px-8 py-4 rounded-lg text-base transition-all duration-150 "
              style={{ 
                backgroundColor: colors.accent,
                color: colors.onAccent
              }}
            >
              <span>Try it free</span>
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
            <p 
              className="mt-4 text-sm"
              style={{ color: colors.textMuted }}
            >
              No credit card. No spam. Just space to grow.
            </p>
          </div>
        </div>
      </section>

      {/* Simple Visual Break - Wave-inspired line */}
      <div className="px-6 max-w-4xl mx-auto relative z-10">
        <div 
          className="h-px w-full"
          style={{ 
            backgroundColor: colors.border 
          }}
        />
      </div>

      {/* What it actually does - Clear, honest */}
      <section className="px-6 py-16 md:py-24 max-w-4xl mx-auto relative z-10">
        <h2 
          className="text-center text-xl md:text-2xl mb-12 font-semibold tracking-[-0.035em]"
          style={{ color: colors.textPrimary }}
        >
          Build the habit. Skip the schedule.
        </h2>

        <div className="grid md:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <div 
              key={index}
              className="text-left pt-4 pb-2 transition-all duration-200"
              style={{ 
                borderTop: `1px solid ${colors.textPrimary}`,
                transitionDelay: `${index * 100}ms`
              }}
            >
              <div 
                className="inline-flex items-center justify-center w-10 h-10 mb-4"
                style={{ border: `1px solid ${colors.border}` }}
              >
                <benefit.icon size={22} strokeWidth={1.5} style={{ color: colors.textSecondary }} />
              </div>
              <h3 
                className="text-lg mb-2 font-semibold tracking-tight"
                style={{ color: colors.textPrimary }}
              >
                {benefit.title}
              </h3>
              <p 
                className="text-sm leading-relaxed"
                style={{ color: colors.textMuted }}
              >
                {benefit.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Concrete Examples - What surfers actually track */}
      <section className="px-6 py-16 max-w-4xl mx-auto relative z-10">
        <h2 
          className="text-center text-xl md:text-2xl mb-4 font-semibold tracking-[-0.035em]"
          style={{ color: colors.textPrimary }}
        >
          What surfers track
        </h2>
        <p 
          className="text-center text-sm mb-12"
          style={{ color: colors.textMuted }}
        >
          Real examples. Pick one that resonates.
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Habits Column */}
          <div 
            className="pt-4 pb-2"
            style={{ borderTop: `1px solid ${colors.textPrimary}`,
            }}
          >
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 size={18} style={{ color: colors.accent }} />
              <span className="text-sm font-medium" style={{ color: colors.textPrimary }}>Habits</span>
            </div>
            <div className="space-y-3">
              {[
                { habit: "Stretch after every session", streak: 12 },
                { habit: "10 breath holds before bed", streak: 8 },
                { habit: "Check the forecast", streak: 23 },
              ].map((item, i) => (
                <div 
                  key={i}
                  className="flex items-center justify-between p-3 rounded-md"
                  style={{ backgroundColor: colors.bg }}
                >
                  <span className="text-sm" style={{ color: colors.textSecondary }}>{item.habit}</span>
                  <div 
                    className="flex items-center gap-1 text-xs px-2 py-1 rounded-full"
                    style={{ 
                      backgroundColor: `${colors.warning}1a`,
                      color: colors.warning
                    }}
                  >
                    <Flame size={12} />
                    <span>{item.streak}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tasks Column */}
          <div 
            className="pt-4 pb-2"
            style={{ borderTop: `1px solid ${colors.textPrimary}`,
            }}
          >
            <div className="flex items-center gap-2 mb-4">
              <ClipboardList size={18} style={{ color: colors.textSecondary }} />
              <span className="text-sm font-medium" style={{ color: colors.textPrimary }}>Tasks</span>
            </div>
            <div className="space-y-3">
              {[
                { task: "Fix ding on the nose", priority: "Today" },
                { task: "Order new leash", priority: "This week" },
                { task: "Research Bali surf camps", priority: "Someday" },
              ].map((item, i) => (
                <div 
                  key={i}
                  className="flex items-center justify-between p-3 rounded-md"
                  style={{ backgroundColor: colors.bg }}
                >
                  <span className="text-sm" style={{ color: colors.textSecondary }}>{item.task}</span>
                  <span 
                    className="text-xs px-2 py-1 rounded-full"
                    style={{ 
                      backgroundColor: `${colors.accent}15`,
                      color: colors.accent
                    }}
                  >
                    {item.priority}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Goals Column */}
          <div 
            className="pt-4 pb-2"
            style={{ borderTop: `1px solid ${colors.textPrimary}`,
            }}
          >
            <div className="flex items-center gap-2 mb-4">
              <Target size={18} style={{ color: colors.textSecondary }} />
              <span className="text-sm font-medium" style={{ color: colors.textPrimary }}>Goals</span>
            </div>
            <div className="space-y-3">
              {[
                { goal: "Surf 50 sessions this year", progress: 34, total: 50 },
                { goal: "30 dawn patrols this season", progress: 18, total: 30 },
                { goal: "Surf 5 new breaks", progress: 2, total: 5 },
              ].map((item, i) => (
                <div 
                  key={i}
                  className="p-3 rounded-md"
                  style={{ backgroundColor: colors.bg }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm" style={{ color: colors.textSecondary }}>{item.goal}</span>
                    <span className="text-xs" style={{ color: colors.textSecondary }}>{item.progress}/{item.total}</span>
                  </div>
                  <div 
                    className="h-1.5 rounded-full overflow-hidden"
                    style={{ backgroundColor: `${colors.border}50` }}
                  >
                    <div 
                      className="h-full rounded-full"
                      style={{ 
                        width: `${(item.progress / item.total) * 100}%`,
                        backgroundColor: colors.textSecondary
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Events & Quotes Column */}
          <div 
            className="pt-4 pb-2"
            style={{ borderTop: `1px solid ${colors.textPrimary}`,
            }}
          >
            <div className="flex items-center gap-2 mb-4">
              <CalendarDays size={18} style={{ color: colors.textSecondary }} />
              <span className="text-sm font-medium" style={{ color: colors.textPrimary }}>Events & Quotes</span>
            </div>
            <div className="space-y-3">
              {/* Events */}
              <div 
                className="flex items-center gap-3 p-3 rounded-md"
                style={{ backgroundColor: colors.bg }}
              >
                <Sunrise size={16} style={{ color: colors.textSecondary }} />
                <div>
                  <span className="text-sm block" style={{ color: colors.textSecondary }}>Dawn patrol with crew</span>
                  <span className="text-xs" style={{ color: colors.textMuted }}>Tomorrow, 5:30 AM</span>
                </div>
              </div>
              <div 
                className="flex items-center gap-3 p-3 rounded-md"
                style={{ backgroundColor: colors.bg }}
              >
                <CloudSun size={16} style={{ color: colors.textSecondary }} />
                <div>
                  <span className="text-sm block" style={{ color: colors.textSecondary }}>Swell hitting Friday</span>
                  <span className="text-xs" style={{ color: colors.textMuted }}>Friday, All day</span>
                </div>
              </div>
              {/* Daily Quote */}
              <div 
                className="p-3 rounded-md border-l-2"
                style={{ 
                  backgroundColor: colors.bg,
                  borderLeftColor: colors.accent
                }}
              >
                <Quote size={14} style={{ color: colors.textSecondary }} className="mb-1" />
                <p className="text-xs italic leading-relaxed" style={{ color: colors.textMuted }}>
                  "The sea lives in every one of us."
                </p>
                <span className="text-xs" style={{ color: colors.textMuted }}>- Robert Wyland</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The "Wait, I can use this" moment */}
      <section className="px-6 py-16 max-w-3xl mx-auto relative z-10">
        <div 
          className="pt-4 pb-2"
          style={{ borderTop: `1px solid ${colors.textPrimary}`,
            
          }}
        >
          <Sparkles size={28} style={{ color: colors.textSecondary }} className="mx-auto mb-6" />
          
          <h3 
            className="text-xl md:text-2xl mb-6 font-semibold tracking-[-0.035em]"
            style={{ color: colors.textPrimary }}
          >
            Everything you need. Nothing you don't.
          </h3>

          <div className="space-y-4 text-left max-w-md mx-auto mb-8">
            {[
              "Track up to 5 habits",
              "See your streaks build over time",
              "Set a few goals that actually matter",
              "No notifications unless you want them",
              "Works on your phone, tablet, anywhere",
              "Free forever. Upgrade only if you want more."
            ].map((item, i) => (
              <div 
                key={i}
                className="flex items-center gap-3"
              >
                <div 
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${colors.accent}20` }}
                >
                  <CheckCircle2 size={12} style={{ color: colors.accent }} />
                </div>
                <span className="text-sm" style={{ color: colors.textSecondary }}>
                  {item}
                </span>
              </div>
            ))}
          </div>

          <p 
            className="text-sm mb-8"
            style={{ color: colors.textMuted }}
          >
            Need more? Upgrade anytime for $5/month. Cancel anytime.
          </p>

          <button
            onClick={onGetStarted}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm transition-all duration-150 "
            style={{ 
              backgroundColor: 'transparent',
              color: colors.accent,
              border: `1px solid ${colors.accent}50`
            }}
          >
            Start with free
            <ArrowRight size={16} />
          </button>
        </div>
      </section>

      {/* Trust signals - Understated */}
      <section className="px-6 py-12 max-w-4xl mx-auto relative z-10">
        <div className="flex flex-wrap justify-center gap-8 text-center">
          {[
            { icon: Shield, text: "Your data stays private" },
            { icon: Smartphone, text: "Works everywhere" },
            { icon: Moon, text: "Dark mode included" }
          ].map((item, i) => (
            <div 
              key={i}
              className="flex items-center gap-2"
              style={{ color: colors.textMuted }}
            >
              <item.icon size={16} />
              <span className="text-sm">{item.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Final gentle nudge */}
      <section className="px-6 py-16 md:py-24 max-w-3xl mx-auto text-center relative z-10">
        <p 
          className="text-lg md:text-xl mb-8 leading-relaxed font-semibold tracking-tight"
          style={{ color: colors.textSecondary }}
        >
          The best surfers aren't the most talented.
          <span className="hidden md:inline"><br /></span>
          <span className="inline md:hidden"> </span>
          They're the ones who show up.
        </p>
        
        <button
          onClick={onGetStarted}
          className="group inline-flex items-center gap-3 px-8 py-4 rounded-lg text-base transition-all duration-150 "
          style={{ 
            backgroundColor: colors.accent,
            color: colors.onAccent
          }}
        >
          <span>Start your streak</span>
          <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
        </button>
      </section>

      {/* Footer */}
      <footer className="px-6 py-8 border-t relative z-10" style={{ borderColor: colors.border + '30' }}>
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
          <Logo size="sm" isDark={isDark} />
          
          <div className="flex gap-6">
            <Link 
              to="/privacy" 
              className="text-sm hover:opacity-70 transition-opacity"
              style={{ color: colors.textMuted }}
            >
              Privacy
            </Link>
            <Link 
              to="/terms" 
              className="text-sm hover:opacity-70 transition-opacity"
              style={{ color: colors.textMuted }}
            >
              Terms
            </Link>
            <Link 
              to="/about" 
              className="text-sm hover:opacity-70 transition-opacity"
              style={{ color: colors.textMuted }}
            >
              About
            </Link>
          </div>

          <p className="text-xs" style={{ color: colors.textMuted }}>
            © 2026 mind.
          </p>
        </div>
      </footer>

    </div>
  );
};

export default SurferLandingPage;
